import React from 'react';

import Compass from './Compass';
import HumidityRing from './HumidityRing';
import PressureGauge from './PressureGauge';
import {
    WindIcon,
    DropIcon,
    GaugeIcon,
    RainIcon,
    SunIcon,
    ShieldIcon,
    HomeIcon
} from '../icons';
import { Language } from '../i18n';
import { WeatherDataRecord } from '../types';
import {
    degToCardinal,
    fahToCelsius,
    formatNumber,
    inHgToHpa,
    inchesToMm,
    mphToKmh,
    mphToMs,
    ratio,
    uvBand
} from '../utils';

const UV_COLORS = ['#4ade80', '#facc15', '#fb923c', '#f87171', '#c084fc'];

interface CardProps {
    data: WeatherDataRecord;
    t: Language;
}

const CardTitle: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
    <h2 className="card-title">{icon}<span>{text}</span></h2>
);

const Value: React.FC<{ value: string; unit?: string; className?: string }> = ({ value, unit, className }) => (
    <div className={`value ${className ?? ''}`}>
        <span key={value} className="tick">{value}</span>
        {unit && <span className="unit">{unit}</span>}
    </div>
);

const WindCard: React.FC<CardProps> = ({ data, t }) => {
    const windDir = data.windDir ?? null;
    const compassLabels = [0, 2, 4, 6].map(i => t.cardinals[i]);

    return (
        <section className="card wide">
            <CardTitle icon={<WindIcon />} text={t.wind} />
            <div className="wind-body">
                <Compass
                    deg={windDir}
                    labels={compassLabels}
                    cardinal={windDir === null ? '—' : degToCardinal(windDir, t.cardinals)}
                    label={windDir === null
                        ? t.windDirectionUnknown
                        : t.windFrom(degToCardinal(windDir, t.cardinalNames), Math.round(windDir))} />
                <div className="wind-stats">
                    <div>
                        <div className="label">{t.speed}</div>
                        <Value value={formatNumber(mphToMs(data.windSpeedMph), 1)} unit={t.metersPerSec} className="md" />
                        <div className="sub">{formatNumber(mphToKmh(data.windSpeedMph), 1)} {t.kmPerHour}</div>
                    </div>
                    <div>
                        <div className="label">{t.gust}</div>
                        <Value value={formatNumber(mphToMs(data.windGustMph), 1)} unit={t.metersPerSec} className="md" />
                        <div className="sub">{formatNumber(mphToKmh(data.windGustMph), 1)} {t.kmPerHour}</div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const HumidityCard: React.FC<CardProps> = ({ data, t }) => (
    <section className="card">
        <CardTitle icon={<DropIcon />} text={t.humidity} />
        <div className="humidity-body">
            <HumidityRing value={data.humidity ?? null} />
        </div>
    </section>
);

const PressureCard: React.FC<CardProps> = ({ data, t }) => {
    const hpa = inHgToHpa(data.baroMin);

    return (
        <section className="card">
            <CardTitle icon={<GaugeIcon />} text={t.pressure} />
            <PressureGauge hpa={hpa} />
            <Value value={formatNumber(hpa, 0)} unit="hPa" className="md center" />
        </section>
    );
};

const RainCard: React.FC<CardProps> = ({ data, t }) => (
    <section className="card">
        <CardTitle icon={<RainIcon />} text={t.rain} />
        <div>
            <Value value={formatNumber(inchesToMm(data.rainIn), 1)} unit={t.millimiters} className="lg" />
            <div className="sub">{t.lastHour}</div>
        </div>
        <div className="pill-row">
            <span>{t.today}</span>
            <b>{formatNumber(inchesToMm(data.dailyRainIn), 1)} {t.millimiters}</b>
        </div>
    </section>
);

const UvCard: React.FC<CardProps> = ({ data, t }) => {
    const uv = data.uv ?? null;
    const band = uvBand(uv);

    return (
        <section className="card">
            <CardTitle icon={<ShieldIcon />} text={t.uvIndex} />
            <div>
                <Value value={formatNumber(uv, 1)} className="lg" />
                <div className="sub uv-level" style={{ color: UV_COLORS[band] }}>{uv === null ? '' : t.uvLevels[band]}</div>
            </div>
            <div className="bar uv-bar">
                <span className="bar-dot-track" style={{ transform: `translateX(${ratio(uv, 0, 11) * 100}%)` }}>
                    <span className="bar-dot" />
                </span>
            </div>
        </section>
    );
};

const SolarCard: React.FC<CardProps> = ({ data, t }) => {
    const solar = data.solarRadiation ?? null;

    return (
        <section className="card">
            <CardTitle icon={<SunIcon />} text={t.solar} />
            <Value value={formatNumber(solar, 0)} unit={'W/m²'} className="lg" />
            <div className="bar solar-bar">
                <span className="bar-fill" style={{ transform: `scaleX(${ratio(solar, 0, 1100)})` }} />
            </div>
        </section>
    );
};

const IndoorCard: React.FC<CardProps> = ({ data, t }) => (
    <section className="card">
        <CardTitle icon={<HomeIcon />} text={t.indoor} />
        <Value value={formatNumber(fahToCelsius(data.indoorTempF), 1)} unit={'°C'} className="lg" />
        <div className="pill-row">
            <DropIcon />
            <b>{formatNumber(data.indoorHumidity, 0)} %</b>
        </div>
    </section>
);

export const WeatherCards: React.FC<CardProps> = ({ data, t }) => (
    <main className="grid">
        <WindCard data={data} t={t} />
        <HumidityCard data={data} t={t} />
        <PressureCard data={data} t={t} />
        <RainCard data={data} t={t} />
        <UvCard data={data} t={t} />
        <SolarCard data={data} t={t} />
        <IndoorCard data={data} t={t} />
    </main>
);

// Placeholders with the same layout while the first reading loads.
export const SkeletonCards: React.FC = () => (
    <main className="grid">
        <div className="card wide skeleton-card" />
        {Array.from({ length: 6 }, (_, i) => <div key={i} className="card skeleton-card" />)}
    </main>
);
